import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, Building2, Users, Target, Megaphone,
  ArrowRight, ArrowLeft, Check, Briefcase, HeadphonesIcon,
  Wrench, MessageSquare
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { cn } from '../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const focusOptions = [
  { value: 'ventas', label: 'Ventas', desc: 'Noah cierra ventas y genera cotizaciones', icon: Target },
  { value: 'servicio_cliente', label: 'Servicio al Cliente', desc: 'Noah resuelve problemas y da soporte', icon: HeadphonesIcon },
  { value: 'soporte_tecnico', label: 'Soporte Tecnico', desc: 'Noah diagnostica y guia soluciones tecnicas', icon: Wrench },
  { value: 'pqr', label: 'PQR', desc: 'Noah gestiona peticiones, quejas y recursos', icon: MessageSquare },
];

const Onboarding = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({
    company_name: '', company_type: '', workers_count: '',
    noah_focus: 'ventas', business_description: '',
  });

  const updateField = (key, val) => setData(prev => ({ ...prev, [key]: val }));

  const saveAndFinish = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/onboarding/save`, { step: 'admin', data });
      toast.success('Configuracion guardada. Bienvenido a DEAL APP!');
      navigate('/');
    } catch (err) {
      toast.error('Error al guardar');
    } finally { setSaving(false); }
  };

  const steps = [
    // Step 0: Welcome
    {
      title: 'Bienvenido a DEAL APP',
      noah: 'Hola, soy Noah, tu asesor comercial. Voy a ayudarte a configurar tu cuenta para que juntos logremos los mejores resultados. Empecemos conociendonos.',
      content: (
        <div className="text-center py-4">
          <div className="w-20 h-20 mx-auto rounded-2xl bg-primary flex items-center justify-center shadow-2xl mb-4">
            <Sparkles className="w-10 h-10 text-white" />
          </div>
          <p className="text-muted-foreground text-sm">Noah te guiara paso a paso</p>
        </div>
      ),
    },
    // Step 1: Company info
    {
      title: 'Tu Empresa',
      noah: 'Cuentame sobre tu empresa. Con esta informacion podre asistirte de manera mas precisa y personalizada.',
      content: (
        <div className="space-y-4">
          <div><Label>Nombre de la empresa</Label><Input value={data.company_name} onChange={e => updateField('company_name', e.target.value)} placeholder="Mi Empresa S.A.S" data-testid="onb-company-name" /></div>
          <div><Label>Tipo de comercio</Label><Input value={data.company_type} onChange={e => updateField('company_type', e.target.value)} placeholder="E-commerce, Servicios, Tecnologia..." data-testid="onb-company-type" /></div>
          <div><Label>Cantidad de trabajadores</Label><Input value={data.workers_count} onChange={e => updateField('workers_count', e.target.value)} placeholder="1-10, 10-50, 50+" data-testid="onb-workers" /></div>
          <div><Label>Descripcion del negocio</Label><Input value={data.business_description} onChange={e => updateField('business_description', e.target.value)} placeholder="Que hace tu empresa?" data-testid="onb-description" /></div>
        </div>
      ),
    },
    // Step 2: Noah focus
    {
      title: 'Enfoque de Noah',
      noah: 'Excelente. Ahora dime, en que area te gustaria que me enfoque principalmente? Puedo adaptarme a diferentes tipos de gestion.',
      content: (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {focusOptions.map(opt => {
            const Icon = opt.icon;
            return (
              <button key={opt.value} data-testid={`onb-focus-${opt.value}`}
                onClick={() => updateField('noah_focus', opt.value)}
                className={cn("p-4 rounded-xl border text-left transition-all",
                  data.noah_focus === opt.value
                    ? "bg-violet-500/15 border-violet-500/40 ring-1 ring-violet-500/30"
                    : "bg-white/5 border-white/10 hover:bg-white/10")}>
                <div className="flex items-center gap-3">
                  <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center",
                    data.noah_focus === opt.value ? "bg-violet-500 text-white" : "bg-white/10 text-muted-foreground")}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{opt.label}</p>
                    <p className="text-[11px] text-muted-foreground">{opt.desc}</p>
                  </div>
                </div>
                {data.noah_focus === opt.value && <Check className="w-4 h-4 text-brand-deep dark:text-brand-deep dark:text-brand-green mt-2 ml-auto" />}
              </button>
            );
          })}
        </div>
      ),
    },
    // Step 3: Ready
    {
      title: 'Todo Listo!',
      noah: `Perfecto${data.company_name ? ', ' + data.company_name : ''}. Ya tengo toda la informacion que necesito. Voy a enfocarme en ${focusOptions.find(f => f.value === data.noah_focus)?.label || 'ventas'} para maximizar tus resultados. Empecemos!`,
      content: (
        <div className="text-center py-6 space-y-4">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-primary flex items-center justify-center shadow-2xl">
            <Check className="w-8 h-8 text-white" />
          </div>
          <div className="space-y-1">
            {data.company_name && <p className="text-sm"><span className="text-muted-foreground">Empresa:</span> {data.company_name}</p>}
            {data.company_type && <p className="text-sm"><span className="text-muted-foreground">Tipo:</span> {data.company_type}</p>}
            <p className="text-sm"><span className="text-muted-foreground">Enfoque Noah:</span> {focusOptions.find(f => f.value === data.noah_focus)?.label}</p>
          </div>
        </div>
      ),
    },
  ];

  const current = steps[step];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="fixed top-0 left-0 right-0 z-50 h-1 bg-muted">
        <motion.div className="h-full bg-primary"
          animate={{ width: `${((step + 1) / steps.length) * 100}%` }} transition={{ duration: 0.5 }} />
      </div>

      <div className="flex-1 flex items-center justify-center p-4 sm:p-6">
        <div className="max-w-lg w-full">
          <AnimatePresence mode="wait">
            <motion.div key={step}
              initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.25 }} className="space-y-6">

              <div className="text-center">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Paso {step + 1} de {steps.length}</p>
                <h1 className="text-2xl sm:text-3xl font-bold">{current.title}</h1>
              </div>

              {/* Noah bubble */}
              <div className="bg-card border border-border rounded-2xl p-5 shadow-lg">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center">
                    <Sparkles className="w-3.5 h-3.5 text-white" />
                  </div>
                  <span className="text-sm font-bold">Noah</span>
                </div>
                <p className="text-sm leading-relaxed text-foreground/85">{current.noah}</p>
              </div>

              {current.content}

              <div className="flex items-center gap-1">
                {steps.map((_, i) => (
                  <div key={i} className={cn("h-1.5 rounded-full transition-all flex-1",
                    i === step ? "bg-violet-500" : i < step ? "bg-violet-500/40" : "bg-muted")} />
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <div className="p-4 sm:p-6 flex items-center justify-between max-w-lg mx-auto w-full">
        <Button variant="ghost" onClick={() => setStep(s => s - 1)} disabled={step === 0} className="gap-1">
          <ArrowLeft className="w-4 h-4" /> Anterior
        </Button>
        <Button variant="ghost" className="text-xs text-muted-foreground" onClick={() => navigate('/')}>Saltar</Button>
        {step === steps.length - 1 ? (
          <Button data-testid="onboarding-finish" onClick={saveAndFinish} disabled={saving}
            className="gap-1 bg-primary">
            <Check className="w-4 h-4" /> {saving ? 'Guardando...' : 'Comenzar'}
          </Button>
        ) : (
          <Button data-testid="onboarding-next" onClick={() => setStep(s => s + 1)}
            className="gap-1 bg-primary">
            Siguiente <ArrowRight className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default Onboarding;
